import React from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { logger } from "../../../utilities/logger";
import authModule from "../../../utilities/AuthModule";
import { RootState } from "../../../src/taskpane/redux";
import { FilterSection } from "../../../src/taskpane/redux/models/reduxModels";
import { IViewsDropdown } from "../../../src/taskpane/interfaces/IViewsDropdown";
import { setContentManagerFilterSections, setPreselectReconcilePending } from "../../../src/taskpane/redux/modules/contentManagerDirectory";
import { toggleReload } from "../../../src/taskpane/redux/modules/reload";
import { probeFilterValueExists } from "../../../src/taskpane/services/SharePointRestServices";
import { setFilterSectionsFromFields } from "../../Panels/CMFileFilterPanel/FileFilterHelper";
import { buildSynthesizeContext } from "../../Panels/CMFileFilterPanel/preselectFilterSynthesizer";
import { useClientTypeContext } from "../../../src/taskpane/components/hooks/useClientTypeContext";
import { parsePreselectFilters } from "../../Panels/CMFileFilterPanel/preselectFilterParser";
import { applyPreselectFilters } from "../../Panels/CMFileFilterPanel/preselectFilterResolver";
import { selectedValueSignature } from "../../Panels/CMFileFilterPanel/preselectFilterReconcile";
import { applyInjectedStrike, collectInjectedProbeTargets } from "../../Panels/CMFileFilterPanel/preselectStrike";
import { preselectFilterStore } from "./preselectFilterStore";

export interface UsePreselectFiltersParams {
  // Field source for the reconcile, chosen by the caller: the setup schema
  // (stable ref, present even when a filtered fetch #1 returns 0 rows) with a
  // fallback to redux filterFields on OneDrive. See ContentBodyFilesFolders.
  filterFields: any[];
  isInOneDrive: boolean;
  serverRelPath: string;
  selectedView?: IViewsDropdown;
  meText: string;
  onApplied: () => void;
  // Called synchronously just before a corrective re-fetch is triggered, so the
  // caller can raise its grid fetch indicator (setIsFetchingData(true)) and keep
  // the shimmer continuous across the reload instead of flashing the fetch #1 grid.
  onReloadStart?: () => void;
}

/**
 * Reconciles a pre-selected (querystring) filter against the library's REAL filter
 * values, then confirms or corrects the fast (schema-only) result:
 *  - On SharePoint the fast path (useContentManagerBody) already synthesized the
 *    sections and filtered fetch #1, and set `preselectReconcilePending` + a fast
 *    query signature. This hook rebuilds the real sections via RenderListFilterData,
 *    re-applies the querystring, and re-fetches ONLY if the effective query changed
 *    (a token correction).
 *  - On OneDrive (no early schema) there is no fast path: `filterSections` is empty
 *    and no fast signature exists, so this behaves like the original reactive path
 *    and re-fetches once when the querystring resolves to a selection.
 * In both cases the final sections come from the authoritative real values, so the
 * outcome matches a manual selection for every field type.
 *
 * Injected values (requested but absent from the built value list) are strike-
 * evaluated here too: each gets a definitive `field = value` existence probe, run
 * concurrently with any corrective re-fetch, and confirmed-absent values are
 * flagged `unavailable` so the panel renders them struck-through.
 */
export const usePreselectFilters = ({
  filterFields,
  isInOneDrive,
  serverRelPath,
  selectedView,
  meText,
  onApplied,
  onReloadStart,
}: UsePreselectFiltersParams): void => {
  const dispatch = useDispatch();
  const reduxStore = useStore<RootState>();

  const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);
  const userAccount = useSelector((state: RootState) => state.user.account);
  const datasourceItems = useSelector((state: RootState) => state.tenantInfo?.datasources);
  const hideTitleFieldFilter = useSelector((state: RootState) => state.tenantInfo?.hideTitleFieldFilter);
  // Must carry the SAME context as the fast path (useContentManagerBody) - a differing
  // datasourceMutated verdict between the two drops preselect sections and double-loads.
  // The hook value alone cannot guarantee that: the async lookup can settle BETWEEN the fast
  // synthesize and this reconcile, so when a fast path ran, the reconcile reuses the exact
  // context snapshot the fast path stored with its signature (see preselectFilterStore).
  const liveClientTypes = useClientTypeContext(currentDirectory?.siteId);
  const reconcilePending = useSelector((state: RootState) => state.contentManagerDirectory.preselectReconcilePending);
  const filterSectionCount = useSelector((state: RootState) => state.contentManagerDirectory.filterSections.length);

  const inFlightRef = React.useRef<boolean>(false);
  const latestDirectoryIdRef = React.useRef<string | undefined>(currentDirectory?.id);
  latestDirectoryIdRef.current = currentDirectory?.id;

  // Unmount tracking for the abandon gate. Deliberately NOT an effect-cleanup
  // flag on the reconcile effect itself: that cleanup also runs on every dep
  // change, and the reconcile's own setPreselectReconcilePending(true) dispatch
  // changes a dep — a per-invocation flag would make every no-fast-path run
  // self-cancel on success and strand the panel Loader with pending stuck true.
  const unmountedRef = React.useRef<boolean>(false);
  React.useEffect(() => () => {
    unmountedRef.current = true;
  }, []);

  // Probes every injected value for existence and applies strike decisions to the
  // CURRENT store sections. Fire-and-forget: runs concurrently with a corrective
  // re-fetch (the probe is a direct `field = value` existence check, independent of
  // the grid result), so strike-through appears as soon as the probes settle.
  const evaluateInjectedStrike = async (sections: FilterSection[], directoryId: string): Promise<void> => {
    try {
      const targets = collectInjectedProbeTargets(sections);
      // OneDrive has no SharePoint REST probe; injected values stay unstruck (fail-open).
      if (targets.length === 0 || isInOneDrive) {
        return;
      }

      const accessToken = await authModule.getSharePointAccessToken();
      const decisions = new Map<string, boolean>();
      await Promise.all(
        targets.map(async target => {
          const exists = await probeFilterValueExists({
            siteUrl: currentDirectory.webUrl,
            listId: currentDirectory.sharePointListId,
            accessToken,
            realFieldName: target.realFieldName,
            filterType: target.type,
            value: target.value,
            serverRelativePath: serverRelPath,
          });
          decisions.set(target.key, !exists);
        }),
      );

      // Abandon if the user navigated away while probing (sections were reset).
      if (latestDirectoryIdRef.current !== directoryId) {
        return;
      }
      const latest = reduxStore.getState().contentManagerDirectory.filterSections;
      const { sections: struck, changed } = applyInjectedStrike(latest, decisions);
      if (changed) {
        dispatch(setContentManagerFilterSections(struck));
      }
    } catch (error) {
      logger(error, "usePreselectFilters (strike evaluation)");
    }
  };

  React.useEffect(() => {
    const raw = preselectFilterStore.read();
    const directoryId = currentDirectory?.id;

    if (raw && directoryId && !preselectFilterStore.isConsumed()) {
      // Pin the target to the directory present when the deep-link filter is first
      // observed — BEFORE the eligibility gate — so a fast navigation away can never
      // let another library become the target.
      preselectFilterStore.bindTargetDirectoryId(directoryId);

      const isTargetDirectory = preselectFilterStore.getTargetDirectoryId() === directoryId;
      const eligible =
        isTargetDirectory &&
        !inFlightRef.current &&
        filterFields?.length > 0 &&
        !!serverRelPath &&
        // Fast path already populated the sections (reconcilePending); OneDrive /
        // no-fast-path starts from an empty section set.
        (reconcilePending || filterSectionCount === 0);

      if (eligible) {
        inFlightRef.current = true;

        // The reconcile owns the panel's section build for its whole run —
        // surface that ownership in redux so the panel shows its Loader and,
        // crucially, gets a reactive edge (true→false) to re-open its build
        // gate when this run settles. Without this, the no-fast-path route
        // (OneDrive) never raises the flag: a hard failure here would flip the
        // store's consumed flag without changing any panel dep, leaving an
        // open panel blocked on a gate nothing re-evaluates. Set BEFORE the
        // async work; every exit below (success, parse-empty, catch) clears it,
        // and navigation / user clear clear it externally.
        if (!reconcilePending) {
          dispatch(setPreselectReconcilePending(true));
        }

        (async () => {
          try {
            const parsed = parsePreselectFilters(raw);
            if (parsed.fields.length === 0) {
              preselectFilterStore.markConsumed();
              dispatch(setPreselectReconcilePending(false));
              return;
            }

            // Signature parity with grid fetch #1: evaluate under the context the fast path
            // used, even if the lookup settled since. Falls back to the live context when no
            // fast path ran (OneDrive / no early schema), where there is no signature to match.
            const clientTypes = preselectFilterStore.getFastClientTypes() ?? liveClientTypes;

            const builtSections = await setFilterSectionsFromFields(
              isInOneDrive,
              oneDriveRootDetails,
              currentDirectory,
              userAccount,
              filterFields,
              hideTitleFieldFilter,
              serverRelPath,
              meText,
              selectedView,
              datasourceItems,
              // Deep-linked fields are exempt from display pruning: losing their
              // section here would read as a query change (corrective unfiltered
              // re-fetch) — see buildDisplayFilterSections.
              parsed.fields.map(field => field.key),
              clientTypes,
            );

            // Abandon if the component unmounted, the directory changed, or the
            // user cleared filters (the clear sites mark the store consumed)
            // during the async build — a late re-publish here would resurrect
            // the deep-link selections over the user's clear. A mere effect
            // re-render (dep change) is NOT an abandon signal — see unmountedRef.
            // Do NOT mark consumed here: a same-target remount can retry.
            if (unmountedRef.current || preselectFilterStore.isConsumed() || preselectFilterStore.getTargetDirectoryId() !== latestDirectoryIdRef.current) {
              return;
            }

            // The fallback re-synthesizes any field whose section the build lost
            // (failed RenderListFilterData call) with the SAME inputs as the fast
            // path — enforced structurally: both sites assemble the context through
            // buildSynthesizeContext — so the signature comparison below still holds.
            const { sections, appliedCount, injectedCount, dropped } = applyPreselectFilters(builtSections, parsed, {
              schemaFields: filterFields,
              ctx: buildSynthesizeContext(datasourceItems, currentDirectory, hideTitleFieldFilter, clientTypes),
            });

            if (dropped.length > 0) {
              // Diagnostic only (kept off the DB logger). "key:value" entries are
              // value-level misses that ARE retained as injected items (the
              // existence probes below decide strike-through); bare-key entries
              // are whole-field drops (unknown / DateTime / excluded fields) that
              // create nothing.
              console.warn(`Preselect filters unresolved: ${dropped.join(", ")}`);
            }

            // Decide whether the reconciled query needs a corrective re-fetch
            // BEFORE clearing the pending flag, so the shimmer can be kept
            // continuous. Fast path present → compare signatures (catches a token
            // correction). No fast path (OneDrive) → re-fetch iff anything resolved.
            const fastSignature = preselectFilterStore.getFastQuerySignature();
            const shouldReload =
              fastSignature === null
                ? appliedCount > 0 || injectedCount > 0
                : fastSignature !== selectedValueSignature(sections);

            // A corrective fetch #2 is coming: raise the caller's fetch indicator
            // NOW, before clearing the pending flag below (the grid's shimmer
            // suppression is derived from it). Otherwise there is a committed frame
            // where suppression is off but fetch #2's own isFetchingData has not
            // engaged yet (its effect runs post-paint), briefly revealing the
            // about-to-be-discarded fetch #1 grid — the visible "double shimmer"
            // flash on token-corrected (User/Lookup/Taxonomy) and other re-fetched
            // fields. When no re-fetch is needed, fetch #1's grid is authoritative
            // and paints immediately.
            if (shouldReload) {
              onReloadStart?.();
            }

            // Always publish the authoritative real sections (replacing the fast
            // synthesized set) so the panel shows real values + injected values.
            dispatch(setContentManagerFilterSections(sections));
            dispatch(setPreselectReconcilePending(false));

            if (appliedCount > 0 || injectedCount > 0) {
              onApplied();
            }

            if (shouldReload) {
              dispatch(toggleReload());
            }

            preselectFilterStore.markConsumed();

            if (injectedCount > 0) {
              void evaluateInjectedStrike(sections, directoryId);
            }
          } catch (error) {
            logger(error, "usePreselectFilters (reconcile)");
            preselectFilterStore.markConsumed(); // never retry a hard failure in a loop
            dispatch(setPreselectReconcilePending(false));
          } finally {
            inFlightRef.current = false;
          }
        })();
      }
    }
    // No effect cleanup on purpose: cleanup also runs on dep changes (including
    // this run's own pending(true) dispatch), and treating that as an abandon
    // signal self-cancelled every no-fast-path run. Unmount is tracked by
    // unmountedRef above; user clears and directory changes have their own
    // signals in the abandon gate.
  }, [filterFields, currentDirectory?.id, serverRelPath, filterSectionCount, reconcilePending]);
};
